<template>
  <div>
    <!-- SELECTOR STARTS HERE -->
    <div class="sticky">
      <div class="row border-bottom white-bg dashboard-header">
        <div class="header-item">
          <span class="fa fa-fw fa-building"></span>
          <strong>View by</strong>
          <el-select size="small" v-model="currentFilter" :value="null">
            <el-option
              v-for="(type, index) in [{ option: 'Station' }, { option: 'Show' }]"
              :key="index"
              :label="type.option"
              :value="type.option"
            />
          </el-select>
        </div>
        <div class="header-item" v-if="selectedFilter === 'Show'">
          <span class="fa fa-fw fa-clock-o"></span>
          <strong>Show |</strong>
          <el-select size="small" v-model="selectedTimeslot" :value="null">
            <el-option
              v-for="slot in timeslots"
              :key="slot.id"
              :label="slot.title"
              :value="slot.id"
            />
          </el-select>
        </div>
      </div>
    </div>
    <!-- SELECTOR ENDS HERE -->
    <div v-if="filtersLoading" class="dashboard-content">
      <div class="row">
        <div class="col-lg-12">
          <loader :message="loadingMessage" />
        </div>
      </div>
    </div>

    <div v-else class="dashboard-content">
      <!-- ANALYTICS UNIT BLOCKS START HERE -->
      <div class="unit-block-row">
        <div class="row row-1">
          <div class="col-lg-3">
            <analytics-unit-block
              :period="selectedProgrammingDate | moment('dddd Do')"
              :unit="'Day'"
              :unitClass="'label-info'"
              :description="'Received'"
              :selected="selectedProgrammingPeriod === 'day'"
              :received="chartsData['day-stats'].total"
              :growth="chartsData['day-stats'].growth"
              :stats="chartsData['day-stats'].points"
              @previous="$emit('previous', { unit: 'day' })"
              @next="$emit('next', { unit: 'day' })"
              @click.native="changePeriod('day')"
            >
              <content-loader-placeholder
                @onNoContent="handleNoContent"
                v-if="!Boolean(chartsData['day-stats'].points.length)"
                :type="'AnalyticsUnitBlock'"
                slot="content-loader"
                :contentVisible="showNoContent"
              ></content-loader-placeholder>
            </analytics-unit-block>
          </div>
          <div class="col-lg-3">
            <analytics-unit-block
              :period="selectedProgrammingDate | moment('YYYY [Week] WW')"
              :unit="'Week'"
              :unitClass="'label-success'"
              :description="'Received'"
              :selected="selectedProgrammingPeriod === 'week'"
              :received="chartsData['week-stats'].total"
              :growth="chartsData['week-stats'].growth"
              :stats="chartsData['week-stats'].points"
              @previous="$emit('previous', { unit: 'week' })"
              @next="$emit('next', { unit: 'week' })"
              @click.native="changePeriod('week')"
            >
              <content-loader-placeholder
                v-if="!Boolean(chartsData['week-stats'].points.length)"
                :type="'AnalyticsUnitBlock'"
                slot="content-loader"
              ></content-loader-placeholder>
            </analytics-unit-block>
          </div>
          <div class="col-lg-3">
            <analytics-unit-block
              :period="selectedProgrammingDate | moment('MMMM')"
              :unit="'Month'"
              :unitClass="'label-primary'"
              :description="'Received'"
              :selected="selectedProgrammingPeriod === 'month'"
              :received="chartsData['month-stats'].total"
              :growth="chartsData['month-stats'].growth"
              :stats="chartsData['month-stats'].points"
              @previous="$emit('previous', { unit: 'month' })"
              @next="$emit('next', { unit: 'month' })"
              @click.native="changePeriod('month')"
            >
              <content-loader-placeholder :type="'AnalyticsUnitBlock'" slot="content-loader"></content-loader-placeholder>
            </analytics-unit-block>
          </div>
          <div class="col-lg-3">
            <analytics-unit-block
              :period="selectedProgrammingDate | moment('YYYY')"
              :unit="'Year'"
              :unitClass="'label-danger'"
              :description="'Received'"
              :selected="selectedProgrammingPeriod === 'year'"
              :received="chartsData['year-stats'].total"
              :growth="chartsData['year-stats'].growth"
              :stats="chartsData['year-stats'].points"
              @previous="$emit('previous', { unit: 'year' })"
              @next="$emit('next', { unit: 'year' })"
              @click.native="changePeriod('year')"
            >
              <content-loader-placeholder
                v-if="!Boolean(chartsData['year-stats'].points.length)"
                :type="'AnalyticsUnitBlock'"
                slot="content-loader"
              ></content-loader-placeholder>
            </analytics-unit-block>
          </div>
        </div>
      </div>
      <!-- ANALYTICS UNIT BLOCKS END HERE -->
      <div class="row row-2">
        <div class="col-md-4">
          <programming-dashboard-unit
            title="Messages"
            titleIconClass="fa fa-fw fa-envelope-o"
            height="280px"
            :titleRightIcon="{
              icon: 'fa fa-info-circle',
              tooltip: 'Click to view more info about messages',
            }"
            @unitRightIconClick="messagesUnitRightIconClick"
          >
            <div v-if="messagesData.total" slot="unit-content">
              <horizontal-bar-chart
                style="max-height: 120px; min-height: 120px;"
                :showYAxes="true"
                :showXAxes="false"
                :data="sortMessagesChartData"
              />
            </div>

            <content-loader-placeholder
              v-if="!Boolean(messagesData.total)"
              slot="unit-content"
              :type="'ProgrammingDashboardUnitLoader'"
            ></content-loader-placeholder>
          </programming-dashboard-unit>
        </div>
        <div class="col-md-4">
          <el-dialog title="Competitions" :visible.sync="promotionsDialogVisible" width="30%">
            <loader v-if="loadingAudienceData" :message="'Loading...'" />
            <promotions-table v-else :data="promotionsData" />
          </el-dialog>

          <programming-dashboard-unit
            title="Competitions"
            titleIconClass="fa fa-fw fa-gift"
            height="280px"
            :titleRightIcon="{
              icon: 'fa fa-info-circle',
              tooltip: 'Click to view Competitions table report',
            }"
            @unitRightIconClick="promotionsUnitRightIconClick"
          >
            <div v-if="promotionsData.length" slot="unit-content">
              <horizontal-bar-chart
                style="max-height: 180px; min-height: 180px; overflow-y: scroll;"
                :height="promotionBarChartHeight"
                :showYAxes="true"
                :showXAxes="false"
                :data="
                  promotionsData.map(p => [
                    getPromotionLabel(p.name),
                    { Entries: p.entries, Uniques: p.uniques },
                  ])
                "
              />
            </div>

            <content-loader-placeholder
              v-if="!Boolean(Object.keys(promotionsData).length)"
              slot="unit-content"
              :type="'ProgrammingDashboardUnitLoader'"
              :contentVisible="showNoContent"
              @onNoContent="handleNoContent"
            ></content-loader-placeholder>
          </programming-dashboard-unit>
        </div>

        <div class="col-md-4">
          <i-box :contentHeight="237">
            <span slot="title">
              <h5 class="unit-title">
                <span class="fa fa-fw fa-calendar"></span>Heat Map
              </h5>
            </span>
            <span slot="content">
              <analytics-heat-map
                v-if="Boolean(chartsData['heat-map-stats'].points)"
                :data="chartsData['heat-map-stats'].points"
                :range="chartsData['heat-map-stats'].range"
                @onChangeDate="$emit('onHeatMapDateChange', $event)"
              ></analytics-heat-map>

              <content-loader-placeholder
                v-if="!Boolean(chartsData['heat-map-stats'].points)"
                :type="'AnalyticsHeatMapLoader'"
              ></content-loader-placeholder>
            </span>
          </i-box>
        </div>
      </div>
      <!--TODO:  Formate graph-->
      <div class="row row-3">
        <div class="col-md-8">
          <i-box contentHeight="500">
            <span slot="title">
              <h5 class="chart-title">
                <i class="fa fa-area-chart"></i>
                <strong>Engagement</strong>
              </h5>
              <div class="chart-filters pull-right">
                <label
                  class="label label-primary chart-label"
                  :class="{ active: selectedEngagementsFilter === 'timeslot' }"
                  @click="$emit('changeFilter', { filter: 'timeslot' })"
                >Timeslots</label>
                <label
                  class="label label-primary chart-label"
                  :class="{ active: selectedEngagementsFilter === 'channel' }"
                  @click="$emit('changeFilter', { filter: 'channel' })"
                >Channels</label>
                <label
                  class="label label-primary chart-label"
                  :class="{ active: selectedEngagementsFilter === 'campaign' }"
                  @click="$emit('changeFilter', { filter: 'campaign' })"
                >Campaigns</label>
              </div>
            </span>
            <span slot="content">
              <analytics-time-series-area-chart
                v-if="Boolean(mainChartData.stats.points.length)"
                :data="mainChartData.stats.points"
                :unit="units[units.indexOf(selectedProgrammingPeriod) - 1]"
                :showLegend="false"
              ></analytics-time-series-area-chart>
              <content-loader-placeholder
                v-if="!Boolean(mainChartData.stats.points.length)"
                slot="unit-content"
                :type="'EngagementGraphLoader'"
              ></content-loader-placeholder>
            </span>
          </i-box>
        </div>

        <div class="col-md-4">
          <programming-dashboard-unit title="Topics Trending" titleIconClass="fa fa-fw fa-star">
            <vue-word-cloud
              v-if="trendingTopics.length"
              slot="unit-content"
              style="width: 100%; min-height: 465px; display: block;"
              font-family="'Helvetica Neue', sans-serif"
              rotationUnit="turn"
              :color="trendingWordColor"
              :rotation="trendingWordsRotation"
              :animationDuration="1500"
              :spacing="0.3"
              :words="trendingTopics"
            ></vue-word-cloud>

            <content-loader-placeholder v-else slot="unit-content" :type="'WordCloudLoader'"></content-loader-placeholder>
          </programming-dashboard-unit>
        </div>
      </div>
      <!-- ANALYTICS FOR STREAMING DATA -->
      <div class="row row-4" v-if="isStreaming">
        <div class="col-lg-12">
          <programming-dashboard-unit
            height="570px"
            title="Streaming Load"
            titleIconClass="fa fa-fw fa-area-chart"
          >
            <div slot="unit-content">
              <div class="col-lg-8" v-loading="loadingStreamingData">
                <el-tabs v-model="streamingFilter" @tab-click="onChangeStreamingFilter">
                  <el-tab-pane label="Full week" name="week"></el-tab-pane>
                  <el-tab-pane label="Week day" name="weekday"></el-tab-pane>
                  <el-tab-pane label="Saturday" name="saturday"></el-tab-pane>
                  <el-tab-pane label="Sunday" name="sunday"></el-tab-pane>
                </el-tabs>
                <streaming-load
                  :showLegend="true"
                  :showGridLines="true"
                  :periodLabel="streamingPeriodLabel"
                  :isTrimesterComplete="isTrimesterComplete"
                  :data="prepareStreamingGraphData"
                />
              </div>
              <div class="col-lg-4">
                <streaming-overview-table
                  :avgData="streamingData"
                  :selectedPeriod="getStreamingOverviewPeriod"
                />
              </div>
            </div>
          </programming-dashboard-unit>
        </div>
      </div>

      <div class="row row-5">
        <div class="col-lg-8">
          <div class="row">
            <div class="col-md-12">
              <programming-dashboard-unit
                title="Commercial Load"
                titleIconClass="fa fa-fw fa-bullhorn"
                height="580px"
              >
                <div slot="unit-content" v-if="commercialLoadData.length">
                  <div class="pull-right">
                    <label>Compare with Station &nbsp;</label>
                    <el-select v-model="currentStation" filterable placeholder="Select">
                      <el-option
                        v-for="station in stations"
                        :key="station.id"
                        :label="station.title"
                        :value="station.id"
                      ></el-option>
                    </el-select>
                  </div>
                  <playout-load
                    :showLegend="true"
                    :station="stationName"
                    :comparedStaton="selectedComparedStation"
                    :data="commercialLoadData"
                    :showGridLines="true"
                    yAxeslabelString="Seconds"
                    xAxeslabelString="Hour"
                    title="commercial"
                  />
                </div>

                <blurred-image-place-holder
                  v-else
                  :showContactSales="true"
                  height="500px"
                  slot="unit-content"
                  :imageSrc="'/static/assets/images/analytics/comercial-load-unblured.png'"
                  :alt="'Comercial load blured image'"
                ></blurred-image-place-holder>
              </programming-dashboard-unit>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <programming-dashboard-unit
            title="Content Threads"
            titleIconClass="fa fa-fw fa-file-text-o"
            height="580px"
          >
            <content-threads-table
              v-if="contentThreads.length"
              slot="unit-content"
              :data="contentThreads"
              :limit="12"
            />

            <content-loader-placeholder v-else slot="unit-content" :type="'ListDividerLoader'"></content-loader-placeholder>
          </programming-dashboard-unit>
        </div>
      </div>
      <div class="row row-6">
        <div class="col-md-6">
          <programming-dashboard-unit title="Musical Load" titleIconClass="fa fa-fw fa-bullhorn">
            <div slot="unit-content" v-if="musicalLoadData.length">
              <div class="pull-right">
                <label>Compare with Station &nbsp;</label>
                <el-select v-model="currentStation" filterable placeholder="Select">
                  <el-option
                    v-for="station in stations"
                    :key="station.id"
                    :label="station.title"
                    :value="station.id"
                  ></el-option>
                </el-select>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <playout-load
                    :showLegend="true"
                    :showGridLines="true"
                    :station="stationName"
                    :comparedStaton="selectedComparedStation"
                    :data="musicalLoadData"
                    yAxeslabelString="No. of songs"
                    xAxeslabelString="Hour"
                    title="music"
                  />
                </div>

                <div class="col-md-12">
                  <div class="row top-track">
                    <div class="col-md-1"></div>
                    <div class="col-md-5 ours">
                      <h3>{{ stationName }}</h3>
                    </div>
                    <div class="col-md-1">
                      <h3>-</h3>
                    </div>
                    <div class="col-md-5 theirs">
                      <h3>{{ selectedComparedStation }}</h3>
                    </div>
                  </div>
                  <div v-for="i in 10" :key="i">
                    <div class="row top-track">
                      <div class="col-md-1">
                        <div class="count">{{ i }}</div>
                      </div>
                      <div class="col-md-5 ours">
                        <span
                          class="artist"
                          v-if="
                            topTracksData.length > 0 &&
                              topTracksData[0].length >= i &&
                              topTracksData[0][i - 1].hasOwnProperty('artist')
                          "
                        >{{ topTracksData[0][i - 1].artist }}</span>
                        <br />
                        <span
                          class="track"
                          v-if="
                            topTracksData.length > 0 &&
                              topTracksData[0].length >= i &&
                              topTracksData[0][i - 1].hasOwnProperty('title')
                          "
                        >{{ topTracksData[0][i - 1].title }}</span>
                        <el-tooltip content="Number of times song played" placement="bottom">
                          <span
                            class="label pull-right"
                            v-if="
                              topTracksData.length > 1 &&
                                topTracksData[0].length >= i &&
                                topTracksData[0][i - 1].hasOwnProperty('artist')
                            "
                          >{{ topTracksData[0][i - 1].count }}</span>
                        </el-tooltip>
                      </div>
                      <div class="col-md-1 status">
                        <growth :is-percentage="false" :value="0"></growth>
                      </div>
                      <div class="col-md-5 theirs">
                        <span
                          class="artist"
                          v-if="
                            topTracksData.length > 1 &&
                              topTracksData[1].length >= i &&
                              topTracksData[1][i - 1].hasOwnProperty('artist')
                          "
                        >{{ topTracksData[1][i - 1].artist }}</span>
                        <br />
                        <span
                          class="track"
                          v-if="
                            topTracksData.length > 1 &&
                              topTracksData[1].length >= i &&
                              topTracksData[1][i - 1].hasOwnProperty('artist')
                          "
                        >{{ topTracksData[1][i - 1].title }}</span>
                        <el-tooltip content="Number of times song played" placement="bottom">
                          <span
                            class="label pull-left"
                            v-if="
                              topTracksData.length > 1 &&
                                topTracksData[1].length >= i &&
                                topTracksData[1][i - 1].hasOwnProperty('artist')
                            "
                          >{{ topTracksData[1][i - 1].count }}</span>
                        </el-tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <blurred-image-place-holder
              v-else
              :showContactSales="true"
              slot="unit-content"
              :imageSrc="'/static/assets/images/analytics/musical-load-unblured.png'"
              :alt="'Musical load blured image'"
            ></blurred-image-place-holder>
          </programming-dashboard-unit>
        </div>
        <div class="col-md-6">
          <programming-dashboard-unit title="Audience Profile" titleIconClass="fa fa-fw fa-user-o">
            <content-placeholder
              v-if="loadingAudienceData"
              style="width: 100%; margin-top: 5%"
              slot="unit-content"
              text-value="Loading..."
              :text-only="true"
              :count="1"
            />
            <div v-else-if="Object.keys(audienceProfileData).length" slot="unit-content">
              <br />
              <div class="row">
                <div class="col-md-2"></div>
                <div class="col-md-3 audience-title">{{ getTimeslotLabel }}</div>
                <div class="col-md-3 audience-title">ALL PROFILES</div>
                <div class="col-md-3 audience-title">STATION AUDIENCE</div>
              </div>
              <div class="row" v-for="demographic in Object.keys(audienceProfileData)" :key="demographic">
                <div class="col-md-2 vertical-text">{{ demographicTitles[demographic] || demographic }}</div>

                <div class="col-md-3">
                  <doughnut-chart
                    v-if="showAudienceChartData(key, 'filtered').length"
                    :height="120"
                    :showLegend="false"
                    :data="audienceProfileData[key].filtered.map(p => [p.title, p.show])"
                  />
                  <doughnut-chart-placeholder v-else></doughnut-chart-placeholder>
                </div>
                <div class="col-md-3">
                  <doughnut-chart
                    v-if="showAudienceChartData(demographic, 'total').length"
                    :height="120"
                    :showLegend="false"
                    :data="audienceProfileData[demographic].total.map(p => [p.title, p.show])"
                  />
                  <doughnut-chart-placeholder v-else></doughnut-chart-placeholder>
                </div>
                <div class="col-md-3">
                  <doughnut-chart
                    v-if="
                      showAudienceChartData(demographic, 'external').length &&
                        showAudienceChartData(demographic, 'external')[0].length
                    "
                    :height="120"
                    :showLegend="false"
                    :data="audienceProfileData[demographic].external.map(p => [p.title, p.show])"
                  />
                  <doughnut-chart-placeholder v-else></doughnut-chart-placeholder>
                </div>
              </div>
            </div>

            <content-loader-placeholder v-else slot="unit-content" :type="'AudienceProfileLoader'"></content-loader-placeholder>
          </programming-dashboard-unit>
        </div>
      </div>
    </div>

    <el-dialog title="Messages" :visible.sync="messagesDialogVisible" width="30%">
      <div v-if="!loadingAudienceData">
        <span>
          <strong class="purple">Total:</strong>
          {{ formatNumber(messagesData.total) }}
        </span>
        <br />
        <span>
          <strong class="purple">Uniques:</strong>
          {{ formatNumber(messagesData.uniques) }}
        </span>
      </div>
    
      <loader  v-else :message="'Loading Data'" />
    </el-dialog>
  </div>
</template>

<script>
import IBox from '@/components/common/IBox';

import AnalyticsUnitBlock from '@/components/analytics/AnalyticsUnitBlock';
import AnalyticsChart from '@/components/analytics/charts/AnalyticsChart';
import AnalyticsHeatMap from '@/components/analytics/charts/AnalyticsHeatMap';
import AnalyticsTimeSeriesAreaChart from '@/components/analytics/charts/AnalyticsTimeSeriesAreaChart';

import moment from 'moment';
import accounting from 'accounting';
import VueWordCloud from 'vuewordcloud';
import colorMixin from '@/mixins/colors';

import { pickOne } from '@/helpers/random';

import Growth from '@/components/common/Growth';

import BarChart from '@/components/charts/BarChart';
import DoughnutChart from '@/components/charts/DoughnutChart';
import HorizontalBarChart from '@/components/charts/HorizontalBarChart';
import Loader from '@/components/common/Loader';
import BlurredImagePlaceHolder from '@/components/common/BlurredImagePlaceHolder';
import ContentPlaceholder from '@/components/common/ContentPlaceholder';
import DoughnutChartPlaceholder from '@/components/common/DoughnutChartPlaceholder';

import ContentLoaderPlaceholder from '@/components/common/ContentLoaderPlaceholder';

import ProgrammingDashboardUnit from './programmingDashboard/ProgrammingDashboardUnit';
import AudienceProfileTable from './programmingDashboard/AudienceProfileTable';
import ContentThreadsTable from './programmingDashboard/ContentThreadsTable';
import PromotionsTable from './programmingDashboard/PromotionsTable';
import StreamingOverviewTable from './programmingDashboard/StreamingOverviewTable';
import PlayoutLoad from './programmingDashboard/PlayoutLoad';
import StreamingLoad from './programmingDashboard/StreamingLoad';

const formatNumber = x => accounting.formatNumber(x, 0, ' ');

export default {
  name: 'ProgrammingDashboard',

  mixins: [colorMixin],

  props: {
    selectedEngagementsFilter: {
      type: String,
      default: 'campaign',
      required: true,
    },
    chartsData: {
      type: Object,
      default: () => ({
        'hour-stats': { total: 0, growth: 0, points: [] },
        'day-stats': { total: 0, growth: 0, points: [] },
        'week-stats': { total: 0, growth: 0, points: [] },
        'month-stats': { total: 0, growth: 0, points: [] },
        'year-stats': { total: 0, growth: 0, points: [] },
        'unique-stats': { 'total-vs-unique': [] },
        'campaign-stats': { points: [[]] },
        'channel-type-stats': { points: [] },
        'heat-map-stats': { points: {}, range: [] },
      }),
      required: true,
    },
    mainChartData: {
      type: Object,
      default: () => ({
        stats: { points: [] },
      }),
      required: true,
    },

    stationName: {
      type: String,
      default: '',
    },

    timeslots: {
      type: Array,
      default: () => [],
    },

    timeslot: {
      type: Number,
      default: null,
    },

    date: {
      type: Object,
      default: () => moment(),
      required: true,
    },

    period: {
      type: String,
      default: 'month',
    },

    activeRange: {
      type: Object,
      default: null,
    },

    streamingData: {
      type: Object,
      default: () => ({
        streaming: {
          averageListeners: [],
          averageListenersTrimester: [],
        },
      }),
    },

    messagesData: {
      type: Object,
      default: () => ({
        chartData: [],
      }),
    },

    stations: {
      type: Array,
      default: () => [],
    },

    selectedStation: {
      type: Number,
      default: null,
    },
    isStreaming: {
      type: Boolean,
      default: false,
    },

    /**
     * Expected Data:
     * {
     *  age: [{ title: '13-18', show: '25%', profile: '19%'}],
     *  gender: [{ title: 'Male', show: '42%', profile: '48%' }],
     * }
     */
    audienceProfileData: {
      type: Object,
      default: () => ({}),
    },

    contentThreadsData: {
      type: Array,
      default: () => [],
    },

    promotionsData: {
      type: Array,
      default: () => [],
    },

    commercialLoadData: {
      type: Array,
      default: () => [],
    },

    musicalLoadData: {
      type: Array,
      default: () => [],
    },

    topTracksData: {
      type: Array,
      default: () => [],
    },

    filtersLoading: {
      type: Boolean,
      default: true,
    },

    dataLoading: {
      type: Boolean,
      default: false,
    },
    loadingStreamingData: {
      type: Boolean,
      default: false,
    },
    loadingPlugins: {
      type: Boolean,
      default: true,
    },
    afstereoDataNotFound: {
      type: Boolean,
    },
    loadingMessage: {
      type: String,
      default: 'loading',
    },

    afstereoPluginEnabled: {
      type: Boolean,
      default: true,
    },
    loadingAudienceData: {
      type: Boolean,
      default: true,
    },
  },

  data: () => ({
    accounting,
    formatNumber,
    selectedFilter: 'Station',
    promotionsDialogVisible: false,
    messagesDialogVisible: false,
    streamingPeriodLabel: 'Full week average',
    isTrimesterComplete: false,
    selectedComparedStation: 'Select a station to compare',
    units: ['minute', 'hour', 'day', 'week', 'month', 'year'],
    streamingFilter: 'week',
    showNoContent: true,
    promotionBarChartHeight: 100,
    demographicTitles: {
      gender: 'Gender',
      birth_date: 'Age',
      ethnicity: 'Race',
    },

  }),
  computed: {
    sortMessagesChartData() {
      return this.messagesData.chartData.sort((a, b) => b[1].unique - a[1].unique);
    },
    processChannelStats() {
      if (!this.chartsData['channel-type-stats'].points.length) {
        return [];
      }
      const points = this.chartsData['channel-type-stats'].points[0];
      return Object.keys(points).map(key => [key, points[key]]);
    },
    contentThreads() {
      return this.contentThreadsData.map(x => ({ ...x, impactors: formatNumber(x.weight) }));
    },

    trendingTopics() {
      return this.contentThreads.map(x => [x.name, x.weight]);
    },

    selectedProgrammingDate: {
      get() {
        return this.date;
      },
      set(value) {
        this.$emit('onDateChange', value);
      },
    },

    prepareStreamingGraphData() {
      const data = this.streamingData.streaming;
      this.isTrimesterComplete = data.isTrimesterComplete;
      return [data.averageListeners, data.averageListenersTrimester];
    },

    selectedProgrammingPeriod: {
      get() {
        return this.period;
      },
      set(value) {
        this.$emit('onPeriodChange', value);
      },
    },

    selectedTimeslot: {
      get() {
        return this.timeslot;
      },
      set(value) {
        this.$emit('onTimeslotChange', value);
      },
    },

    currentStation: {
      get() {
        return this.selectedStation;
      },
      set(value) {
        this.$emit('onStationChange', value);
        const isSameStationId = station => station.id === value;
        const getStationTitle = arr => (arr ? arr.filter(isSameStationId)[0].title : '');
        this.selectedComparedStation = getStationTitle(this.stations);
      },
    },

    currentFilter: {
      get() {
        return this.selectedFilter;
      },
      set(value) {
        if (value === 'Station') {
          this.selectedFilter = 'Station';
          this.$emit('onTimeslotChange', 0);
        } else {
          this.selectedFilter = 'Show';
          this.$emit('onTimeslotChange', this.selectedTimeslot);
        }
      },
    },
    getTimeslotLabel() {
      const period = `FILTERED FOR: ${this.selectedProgrammingPeriod.toUpperCase()}`;
      if (this.timeslot === 0) {
        return period;
      }
      const showTitle = this.timeslots.filter(show => show.id === this.timeslot)[0].title;
      return `${period} - ${showTitle.toUpperCase()}`;
    },
    getStreamingOverviewPeriod() {
      const selectedDate = this.selectedProgrammingDate;
      let formatedDate = '';
      switch (this.period) {
        case 'day':
          formatedDate = selectedDate.format('dddd Do');
          break;
        case 'week':
          formatedDate = selectedDate.format('YYYY [Week] WW');
          break;
        case 'month':
          formatedDate = selectedDate.format('MMMM');
          break;
        case 'year':
          formatedDate = selectedDate.format('YYYY');
          break;
        default:
          formatedDate = 'period not found';
      }
      return formatedDate;
    },
  },

  methods: {
    showAudienceChartData(key, data) {
      const dummyData = [];
      try {
        const audienceData = this.audienceProfileData[key][data];
        const chartData = audienceData.length
          ? audienceData.map(p => this.extractProfileKeyValues(p))
          : dummyData;
        return chartData;
      } catch (error) {
        return dummyData;
      }
    },
    extractProfileKeyValues(data) {
      const hasData = data.show > 0;
      return hasData ? [data.title, data.show] : [];
    },
    trendingWordsRotation() {
      return pickOne([0, 1 / 4]);
    },
    trendingWordColor(item) {
      return this.generateTitleColor(item[0]);
    },

    promotionsUnitRightIconClick() {
      this.promotionsDialogVisible = !this.promotionsDialogVisible;
    },

    messagesUnitRightIconClick() {
      this.messagesDialogVisible = !this.messagesDialogVisible;
    },

    getCorrectDate(date) {
      const toMoment = moment(moment(date));
      return toMoment.add(1, 'd').format('YYYY-MM-DD');
    },

    changePeriod(period) {
      this.$emit('onPeriodChange', period);
    },

    onChangeStreamingFilter(tab) {
      this.streamingPeriodLabel = `${tab.label} average`;
      this.$emit('onStreamingPeriodChange', tab.name);
    },

    getPromotionLabel(label) {
      // We spliting long promotional labels After every third space
      // (promoLabel) Vuejs charts takes each index in the array as a new line
      const unSplitPromLabel = label.trim();
      if (unSplitPromLabel.length > 22) {
        return this.spliter(unSplitPromLabel, 3);
      }
      return [unSplitPromLabel];
    },

    handleNoContent(value) {
      this.showNoContent = value;
    },

    spliter(promo, spaces) {
      let n = spaces;
      const splitPromoLabel = [];
      const splitLabel = promo.split(' ');
      for (let i = 0; i < splitLabel.length - 1; i += spaces) {
        splitPromoLabel.push(splitLabel.slice(i, n).join(' '));
        n += spaces;
      }
      return splitPromoLabel;
    },
  },

  watch: {
    activeRange() {
      if (this.period === 'week') {
        this.activeRange.start = this.getCorrectDate(this.activeRange.start);
        this.activeRange.end = this.getCorrectDate(this.activeRange.end);
      }
    },
    promotionsData() {
      const labelHeight = 100;
      this.promotionBarChartHeight = this.promotionsData.length * labelHeight;
    },
  },

  components: {
    Loader,
    BlurredImagePlaceHolder,
    ContentPlaceholder,
    Growth,
    BarChart,
    DoughnutChart,
    HorizontalBarChart,
    ProgrammingDashboardUnit,
    ContentLoaderPlaceholder,
    DoughnutChartPlaceholder,
    AudienceProfileTable,
    ContentThreadsTable,
    PromotionsTable,
    StreamingOverviewTable,
    PlayoutLoad,
    StreamingLoad,
    [VueWordCloud.name]: VueWordCloud,
    IBox,
    AnalyticsUnitBlock,
    AnalyticsChart,
    AnalyticsHeatMap,
    AnalyticsTimeSeriesAreaChart,
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/style/_variables_live.scss';

.unit-block-row {
  padding-top: 20px;
}

.header-item {
  display: inline-block;
  max-height: 1.5em;
  margin-right: 2em;
  font-size: 1.1em;
  color: $colony-purple;

  .fa {
    font-size: 1.3em;
  }
}

.dashboard-content {
  padding: 10px 0;
}

.purple {
  color: $colony-purple;
}

.mask {
  position: absolute;
  width: 100%;
  height: 100%;
  margin: -15px;
  background-color: #eee;
  opacity: 0.8;
  z-index: 10;
  filter: alpha(opacity=70);
}

.top-track {
  font-size: 0.8em;
  padding: 8px 0;
  border-bottom: 1px solid #ddd;
}

.top-track .count {
  width: 1em;
  font-size: 2em;
  margin-right: 1em;
}

.top-track .track {
  font-weight: bold;
}

.top-track .status {
  text-align: center;
}

.top-track .theirs {
  text-align: right;
}

.ours,
.theirs,
.status {
  padding-right: 0;
  padding-left: 0;
}

.chart-label {
  opacity: 0.7;
}

.active {
  opacity: 1;
}

.vertical-text {
  font-weight: bold;
  font-size: 11px;
  height: 120px;
  text-transform: uppercase;
  text-align: center;
  vertical-align: middle;
  line-height: 120px;
  /* Safari */
  -webkit-transform: rotate(-90deg);

  /* Firefox */
  -moz-transform: rotate(-90deg);

  /* IE */
  -ms-transform: rotate(-90deg);

  /* Opera */
  -o-transform: rotate(-90deg);

  /* Internet Explorer */
  filter: progid:DXImageTransform.Microsoft.BasicImage(rotation=3);
}

.audience-title {
  font-weight: bold;
  font-size: 10px;
  padding: 10px;
  text-align: center;
}

.unit-title {
  font-size: 1.1em;
  font-weight: bolder;
  color: $colony-purple;
}

.text-label {
  text-align: center;
}
div.sticky {
  position: -webkit-sticky; /* Safari */
  position: sticky;
  top: 0;
  z-index: 99;
}
.chart-title {
  color: $colony-purple;
}
</style>



// WEBPACK FOOTER //
// ProgrammingDashboard.vue?717de658